import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      const storedUser = localStorage.getItem('at1s_user');
      const token = localStorage.getItem('at1s_token');
      
      if (storedUser && token) {
        try {
          // Validate token by calling /auth/me
          const freshUser = await authAPI.getMe();
          setUser(freshUser);
          localStorage.setItem('at1s_user', JSON.stringify(freshUser));
        } catch {
          // Token expired or invalid — clear session
          localStorage.removeItem('at1s_user');
          localStorage.removeItem('at1s_token');
        }
      }
      setLoading(false);
    };
    restoreSession();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      
      if (response.success) {
        setUser(response.user);
        localStorage.setItem('at1s_user', JSON.stringify(response.user));
        localStorage.setItem('at1s_token', response.token);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid email or password' 
      };
    }
  };

  const register = async (email, password, name, company, phone, address) => {
    try {
      const response = await authAPI.register(email, password, name, company, phone, address);
      
      if (response.success) {
        setUser(response.user);
        localStorage.setItem('at1s_user', JSON.stringify(response.user));
        localStorage.setItem('at1s_token', response.token);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('at1s_user');
    localStorage.removeItem('at1s_token');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('at1s_user', JSON.stringify(userData));
  };

  const value = {
    user,
    token: localStorage.getItem('at1s_token'),
    loading,
    login,
    register,
    logout,
    updateUser,
    isAdmin: user?.role === 'admin',
    isCustomer: user?.role === 'customer'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};